import z from 'zod';
import { DisplayData, ProviderStaticConfig } from '~/lib/model';
import { coerceIsoDate } from '~/lib/zod';
import { CustomerUserSchema } from '~/lib/schema/customer';
import { TenantOrderContext } from '~/tenants/common/TenantOrderContext';
import { DateTime, Duration } from 'luxon';
import TenantWrapper from '~/tenants/common/TenantWrapper';

export enum PersonaType {
  CUSTOMER = 'customer',
  PROVIDER = 'provider',
  OFFICE = 'office',
  SYSTEM = 'system',
  EDITOR = 'editor',
}

export enum TenantSubscriptionType {
  MICROSITE = 'microsite',
  MATTERPORT = 'matterport',
}

export enum TenantSubscriptionStatus {
  PENDING = 'pending',
  ACTIVE = 'active',
  CANCELLED = 'cancelled',
}

export const TenantSubscriptionSchema = z
  .object({
    status: z.nativeEnum(TenantSubscriptionStatus),
    expires: coerceIsoDate(),
    renew: z.boolean(),
    name: z.string(),
    months: z.number(),
    cost: z.number(),
    order_id: z.string().optional(),
    job_id: z.string().optional(),
    created: coerceIsoDate(),
    payments: z.record(
      z.string(),
      z.object({
        amount: z.number(),
        created: z.number(),
      }),
    ),
  })
  .and(
    z.discriminatedUnion('type', [
      z.object({ type: z.literal(TenantSubscriptionType.MICROSITE), domain: z.string() }),
      z.object({ type: z.literal(TenantSubscriptionType.MATTERPORT), url: z.string() }),
    ]),
  );

export type TenantSubscriptionMetadata = z.output<typeof TenantSubscriptionSchema>;

export type CustomerUserData = z.output<typeof CustomerUserSchema>;

export class TenantProvider<M = {}, Context extends TenantOrderContext = TenantOrderContext> {
  private _short?: string;

  constructor(
    public readonly tenant: TenantWrapper,
    public readonly id: string,
    public readonly config: ProviderStaticConfig & M,
  ) {}

  isWithinTravelTime(_context: Context, _job: Context['jobs'][0], _time: DateTime): string | true {
    return true;
  }

  calculateDuration(order: Context, job: Context['jobs'][0]) {
    const performable = this.tenant.performable(job.performable_id);

    const pjob = new performable.job(order, job.id, job.metadata, this);

    return Duration.fromObject({ minutes: pjob.onsite() });
  }

  get payoutTempId() {
    return `temp-${this.id}`;
  }

  get hidden() {
    return this.config.hidden;
  }

  get location() {
    return this.tenant.location(this.config.location);
  }

  get short() {
    if (!this._short) {
      const name = this.tenant.providers.filter((p) => p.first === this.config.first);
      const nameAndLast = this.tenant.providers.filter(
        (p) => p.first === this.config.first && p.config.last.slice(0, 1) === this.config.last.slice(0, 1),
      );

      if (name.length > 1) {
        if (nameAndLast) {
          this._short = `${this.config.first} ${this.config.last.slice(0, 2)}.`;
        } else {
          this._short = `${this.config.first} ${this.config.last.slice(0, 1)}.`;
        }
      } else {
        this._short = this.config.first;
      }
    }

    return this._short;
  }

  get first() {
    return this.config.first;
  }

  get persona() {
    return this.config.editor ? PersonaType.EDITOR : PersonaType.PROVIDER;
  }

  get phone() {
    return this.config.phone;
  }

  get headshot() {
    return this.config.headshot;
  }

  get name() {
    return `${this.config.first} ${this.config.last}`;
  }

  get performableIds() {
    return [] as string[];
  }

  /**
   * The performables this provider can be scheduled on: what the tenant class
   * declares (`performableIds`, from its per-tenant code flags) PLUS the
   * services ticked on a DB-only provider's profile (`profile_services`, set
   * only by the dynamic overlay for providers with no code record). Additive,
   * so a code provider's answer is exactly `performableIds`. Use this, never
   * `performableIds`, to decide who may take a job.
   */
  get schedulablePerformableIds(): string[] {
    const ids = new Set<string>(this.performableIds ?? []);
    const ticked = this.config.profile_services;

    if (ticked && ticked.length > 0) {
      for (const performable of this.tenant.performables) {
        if (ticked.includes(performable.config.id)) {
          ids.add(performable.config.id);
        }
      }
    }

    return Array.from(ids);
  }

  /** True when a DB-only provider's profile ticks this performable (always false for code providers). */
  protected offersFromProfile(performableId: string): boolean {
    return this.config.profile_services?.includes(performableId) ?? false;
  }

  canPerformCustomer(context: Context, _job: Context['jobs'][0]): true | string {
    // TODO: some times addresses aren't picking the right location when in middle of two locations
    return true;
    //return context.location.slug === this.config.location ? true : 'This provider does not service the order address.';
  }

  canPerform(context: Context, _job: Context['jobs'][0]): true | string {
    // TODO: should be a warning
    //return context.location === this.config.location ? true : 'This provider does not service the order address.';

    return true;
  }
}

export class TenantCustomer<M = {}> {
  constructor(
    public readonly id: string,
    public readonly name: string,
    public readonly metadata: M,
  ) {}

  get info(): DisplayData[] {
    return [];
  }

  get onboarded(): boolean {
    return true;
  }
}
